'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { COOKIE_CONSENT_STORAGE_KEY as STORAGE_KEY, COOKIE_CONSENT_VERSION as CONSENT_VERSION } from '@/app/lib/cookieConsent';

interface ConsentPrefs {
    necessary: true;
    functionality: boolean;
    experience: boolean;
    measurement: boolean;
    targeting: boolean;
}

const DEFAULT_PREFS: ConsentPrefs = {
    necessary: true,
    functionality: false,
    experience: false,
    measurement: false,
    targeting: false,
};

const ALL_ACCEPTED_PREFS: ConsentPrefs = {
    necessary: true,
    functionality: true,
    experience: true,
    measurement: true,
    targeting: true,
};

type CategoryKey = keyof ConsentPrefs;

const CATEGORIES: {
    key: CategoryKey;
    title: string;
    description: string;
    locked?: boolean;
}[] = [
    {
        key: 'necessary',
        title: 'Strictly Necessary',
        description:
            'Required for sokoloco.gg to work at all — keeping you signed in with Kick, protecting your session, and remembering things like your cart and in-progress activity. The site cannot function properly without these, so they can\'t be turned off.',
        locked: true,
    },
    {
        key: 'functionality',
        title: 'Basic Interactions & Functionalities',
        description:
            'Remember your choices as you use the site — sound and volume settings, sidebar layout, and dismissed notices — so you don\'t have to set them again on your next visit.',
    },
    {
        key: 'experience',
        title: 'Experience Enhancement',
        description:
            'Help us tailor how features, content, and layout are presented to you, and let embedded or shared platform components (such as Kick sign-in) work smoothly.',
    },
    {
        key: 'measurement',
        title: 'Measurement',
        description:
            'Help us understand how sokoloco.gg is used — which pages and features get visited and how the site performs — so we can find issues and improve the experience.',
    },
    {
        key: 'targeting',
        title: 'Targeting & Advertising',
        description:
            'Would be used to show you more relevant promotions and to measure how well our marketing performs. We do not currently run third-party advertising trackers; this category is kept in reserve in case that changes.',
    },
];

function ToggleSwitch({
    checked,
    onChange,
    disabled,
}: {
    checked: boolean;
    onChange: (v: boolean) => void;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={() => !disabled && onChange(!checked)}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ${
                checked ? 'bg-emerald-500' : 'bg-gray-700'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                    checked ? 'translate-x-6' : 'translate-x-1'
                }`}
            />
        </button>
    );
}

function readStoredPrefs(): ConsentPrefs | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed.version !== CONSENT_VERSION) return null;
        return { ...DEFAULT_PREFS, ...parsed, necessary: true };
    } catch {
        return null;
    }
}

function storePrefs(prefs: ConsentPrefs) {
    localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...prefs, version: CONSENT_VERSION, updatedAt: new Date().toISOString() })
    );
}

export default function CookieConsentModal() {
    const pathname = usePathname();
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [prefs, setPrefs] = useState<ConsentPrefs>(DEFAULT_PREFS);

    useEffect(() => {
        setMounted(true);
        const stored = readStoredPrefs();
        if (!stored) {
            setVisible(true);
        }
    }, []);

    useEffect(() => {
        if (!visible) return;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [visible]);

    // Let other fixed elements (e.g. the hero's scroll-down arrow) know the
    // bottom bar is on screen so they can avoid visually overlapping it.
    useEffect(() => {
        if (!mounted) return;
        window.dispatchEvent(
            new CustomEvent('sokoloco:cookie-banner', { detail: { visible: visible && !showPreferences } })
        );
    }, [mounted, visible, showPreferences]);

    if (!mounted || !visible || pathname?.startsWith('/raffle-overlay')) return null;

    const acceptAll = () => {
        storePrefs(ALL_ACCEPTED_PREFS);
        setVisible(false);
        setShowPreferences(false);
    };

    const rejectAll = () => {
        storePrefs(DEFAULT_PREFS);
        setVisible(false);
        setShowPreferences(false);
    };

    const savePreferences = () => {
        storePrefs(prefs);
        setVisible(false);
        setShowPreferences(false);
    };

    return (
        <>
            {!showPreferences && (
                <div className="fixed inset-0 z-[180] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
                    <div className="bg-gray-900 shadow-2xl rounded-2xl w-full max-w-lg my-4 sm:my-8 overflow-hidden">
                        <div className="p-6 space-y-5">
                            <h2 className="text-lg font-bold text-white">We value your privacy</h2>
                            <p className="text-sm text-gray-300 leading-relaxed">
                                We and selected third parties use cookies and similar
                                technologies (including local storage) for technical
                                purposes and, with your consent, to enhance your
                                experience, understand how sokoloco.gg is used, and
                                support any future marketing. You can accept, reject, or
                                customize your choices at any time — see our{' '}
                                <Link
                                    href="/terms"
                                    className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 transition-colors duration-200"
                                >
                                    Terms &amp; Conditions
                                </Link>{' '}
                                and{' '}
                                <Link
                                    href="/privacy"
                                    className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 transition-colors duration-200"
                                >
                                    Privacy Policy
                                </Link>{' '}
                                for details.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2.5">
                                <button
                                    onClick={() => setShowPreferences(true)}
                                    className="flex-1 py-3 rounded-xl font-semibold text-sm text-gray-200 bg-gray-800/50 hover:bg-gray-800 transition-colors duration-200"
                                >
                                    Customize
                                </button>
                                <button
                                    onClick={rejectAll}
                                    className="flex-1 py-3 rounded-xl font-semibold text-sm text-gray-200 bg-gray-800/50 hover:bg-gray-800 transition-colors duration-200"
                                >
                                    Reject
                                </button>
                                <button
                                    onClick={acceptAll}
                                    className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 hover:shadow-emerald-500/20 hover:shadow-lg transition-all duration-200"
                                >
                                    Accept
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showPreferences && (
                <div className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
                    <div className="bg-gray-900 shadow-2xl rounded-2xl w-full max-w-2xl my-4 sm:my-8 overflow-hidden max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] flex flex-col">
                        <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 p-6 shrink-0">
                            <h2 className="text-2xl font-bold text-white">
                                Cookie Preferences
                            </h2>
                            <p className="text-green-100 text-sm mt-1">
                                Choose which technologies sokoloco.gg is allowed to use.
                                You can change these settings at any time.
                            </p>
                        </div>

                        <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
                            {CATEGORIES.map((cat) => (
                                <div
                                    key={cat.key}
                                    className="flex items-start justify-between gap-4 pb-5 border-b border-gray-800 last:border-b-0 last:pb-0"
                                >
                                    <div>
                                        <h3 className="text-sm font-semibold text-green-400">
                                            {cat.title}
                                            {cat.locked && (
                                                <span className="ml-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                    Always active
                                                </span>
                                            )}
                                        </h3>
                                        <p className="text-sm text-gray-300 leading-relaxed mt-1">
                                            {cat.description}
                                        </p>
                                    </div>
                                    <ToggleSwitch
                                        checked={prefs[cat.key]}
                                        disabled={cat.locked}
                                        onChange={(v) =>
                                            setPrefs((p) => ({ ...p, [cat.key]: v }))
                                        }
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="p-6 pt-0 shrink-0 flex flex-col sm:flex-row gap-2.5">
                            <button
                                onClick={rejectAll}
                                className="flex-1 py-3 rounded-xl font-bold text-sm text-gray-200 bg-gray-800/50 hover:bg-gray-800 transition-colors duration-200"
                            >
                                Reject
                            </button>
                            <button
                                onClick={savePreferences}
                                className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 hover:shadow-emerald-500/20 hover:shadow-lg transition-all duration-200"
                            >
                                Save &amp; Accept
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
